
import React, { createContext, useContext, useState, useEffect } from 'react';
import { mockIoTGateways as energyGateways } from '@/lib/mock-data/energy/gateways';
import { mockIoTGateways as gasGateways } from '@/lib/mock-data/gas/gateways';
import { mockIoTGateways as waterGateways } from '@/lib/mock-data/water/gateways';
import { useToast } from '@/hooks/use-toast';

export interface Alert {
  id: string;
  deviceId: string;
  deviceType: 'energy' | 'gas' | 'water';
  message: string;
  timestamp: Date;
  read: boolean;
}

interface NotificationsContextType {
  alerts: Alert[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    // Gather all alerts from the gateways
    const allAlerts: Alert[] = [];
    
    // Process energy gateways
    energyGateways.forEach(gateway => {
      gateway.maintenanceStatus.alerts.forEach((message, index) => {
        allAlerts.push({
          id: `energy-${gateway.deviceId}-${index}`,
          deviceId: gateway.deviceId,
          deviceType: 'energy',
          message,
          timestamp: new Date(),
          read: false
        });
      });
    });
    
    // Process gas gateways
    gasGateways.forEach(gateway => {
      gateway.maintenanceStatus.alerts.forEach((message, index) => {
        allAlerts.push({
          id: `gas-${gateway.deviceId}-${index}`,
          deviceId: gateway.deviceId,
          deviceType: 'gas',
          message,
          timestamp: new Date(),
          read: false
        });
      });
    });
    
    // Process water gateways
    waterGateways.forEach(gateway => {
      gateway.maintenanceStatus.alerts.forEach((message, index) => {
        allAlerts.push({
          id: `water-${gateway.deviceId}-${index}`,
          deviceId: gateway.deviceId,
          deviceType: 'water',
          message,
          timestamp: new Date(),
          read: false
        });
      });
    });
    
    // Sort alerts by timestamp (most recent first)
    allAlerts.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    setAlerts(allAlerts);
    
    // Show toast for new alerts
    if (allAlerts.length > 0) {
      toast({
        title: "New Alerts",
        description: `You have ${allAlerts.length} new device alerts`,
        variant: "default",
      });
    }
  }, [toast]);

  const unreadCount = alerts.filter(alert => !alert.read).length;

  const markAsRead = (id: string) => {
    setAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === id ? { ...alert, read: true } : alert
      )
    );
  };

  const markAllAsRead = () => {
    setAlerts(prevAlerts => 
      prevAlerts.map(alert => ({ ...alert, read: true }))
    );
  };

  return (
    <NotificationsContext.Provider value={{ alerts, unreadCount, markAsRead, markAllAsRead }}>
      {children}
    </NotificationsContext.Provider>
  );
};
